// src/components/ErrorBoundary.jsx - Add this to catch React errors

import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error details
    console.error('🚨 React Error Boundary caught an error:', error);
    console.error('📍 Error Info:', errorInfo);
    
    // Track error in analytics if available
    if (window.trackEvent) {
      window.trackEvent('react_error', {
        error_message: error.message,
        error_stack: error.stack,
        component_stack: errorInfo.componentStack,
        error_boundary: this.props.name || 'unknown'
      });
    }

    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      // Custom error UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-20">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h1 className="text-3xl font-bold text-red-600 mb-4">
                🚨 Something went wrong
              </h1>
              <p className="text-gray-600 mb-6">
                We encountered an unexpected error. Don't worry, our team has been notified.
              </p>
              
              {/* Show error details in development */}
              {process.env.NODE_ENV === 'development' && (
                <details className="text-left bg-gray-100 p-4 rounded-lg mb-6">
                  <summary className="cursor-pointer font-medium text-gray-800 mb-2">
                    🔍 Error Details (Development Only)
                  </summary>
                  <div className="text-sm text-red-600 font-mono">
                    <div className="mb-2">
                      <strong>Error:</strong> {this.state.error && this.state.error.toString()}
                    </div>
                    <div className="mb-2">
                      <strong>Component Stack:</strong>
                      <pre className="mt-1 text-xs">{this.state.errorInfo?.componentStack || 'Component stack not available'}</pre>
                     
                    </div>
                  </div>
                </details>
              )}
              
              <div className="space-y-3">
                <button
                  onClick={() => {
                    this.setState({ hasError: false, error: null, errorInfo: null });
                    window.location.reload();
                  }}
                  className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-purple-700 transition-all"
                >
                  🔄 Reload Page
                </button>
                
                <button
                  onClick={() => {
                    window.location.href = '/';
                  }}
                  className="w-full bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-medium hover:bg-gray-300 transition-all"
                >
                  🏠 Go Home
                </button>
              </div>
              
              <div className="mt-6 text-sm text-gray-500">
                <p>If this problem persists, please contact us at hello@connectco.app</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // If no error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;